import React, { Component } from 'react';
import styleable from 'react-styleable';
import PropTypes from 'prop-types';
import { Button } from '@3plearning/react-components';

import BackIcon from '@3plearning/3p-teacheradmin-assets/assets/icons/icon_back.svg';
import InfoIcon from '@3plearning/3p-teacheradmin-assets/assets/icons/icon_information.svg';

import css from './student-data-table.scss';
import { ASSIGNMENT_RESULT_TYPES } from '../../../../../components/assignment-result/assignment-result-reducer';
import { studentDataTranslations } from './student-data-translations';

import {
    getUpdatedCourseLevelState,
    getUpdatedStrandLevelState,
    getUpdatedStandardLevelState,
    getInitialTableProperties,
    getStrandKey,
    getCourseKey,
    getStandardKey,
    hasValidCourses
} from '../../student-helpers';
import { ASSIGNMENT_STATUSES } from '../../../skill-quests-mastery-constants';

import TopicBox from '../topic-box/topic-box';
import ContextModal from '../../../../../components/context-modal/context-modal';

class StudentDataTable extends Component {
    static propTypes = {
        skillQuestsMasteryStudentData: PropTypes.object,
        onCompletedTextClickHandler: PropTypes.func,
        assignmentResultType: PropTypes.number
    };

    constructor(props) {
        super(props);
        this.state = {
            tableProperties: null
        };
    }

    componentDidMount() {
        this.setupTableProperties(this.props.skillQuestsMasteryStudentData);
    }

    componentWillReceiveProps(nextProps) {
        this.manageSkillQuestsMasteryStudentData(nextProps);
    }

    /**
     * DATA MANAGEMENT
     */
    manageSkillQuestsMasteryStudentData = (nextProps) => {
        const {
            skillQuestsMasteryStudentData: updatedSkillQuestsMasteryStudentData
        } = nextProps;
        const { tableProperties } = this.state;

        if (
            updatedSkillQuestsMasteryStudentData &&
            updatedSkillQuestsMasteryStudentData.reportData === null &&
            tableProperties !== null
        ) {
            this.resetTableProperties();
        }

        if (
            updatedSkillQuestsMasteryStudentData &&
            updatedSkillQuestsMasteryStudentData.reportData &&
            tableProperties === null
        ) {
            this.setupTableProperties(updatedSkillQuestsMasteryStudentData);
        }
    };

    /**
     * TABLE PROPERTIES
     */
    resetTableProperties = () => {
        this.setState({
            tableProperties: null
        });
    };

    setupTableProperties = (data) => {
        this.setState({
            tableProperties: getInitialTableProperties(data)
        });
    };

    /**
     * ERROR HANDLING
     */
    hasLoadedInitData = () => {
        const { skillQuestsMasteryStudentData } = this.props;
        const { tableProperties } = this.state;

        return (
            skillQuestsMasteryStudentData &&
            skillQuestsMasteryStudentData.reportData &&
            tableProperties !== null
        );
    };

    /**
     * CLICK HANDLERS
     */
    onCourseClickHandler = (courseKey) => {
        this.setState({
            tableProperties: {
                ...this.state.tableProperties,
                [courseKey]: getUpdatedCourseLevelState(
                    this.state.tableProperties,
                    courseKey
                )
            }
        });
    };

    onStrandClickHandler = (courseKey, strandKey) => {
        this.setState({
            tableProperties: {
                ...this.state.tableProperties,
                [courseKey]: {
                    ...this.state.tableProperties[courseKey],
                    subLevel: {
                        ...this.state.tableProperties[courseKey].subLevel,
                        [strandKey]: getUpdatedStrandLevelState(
                            this.state.tableProperties,
                            courseKey,
                            strandKey
                        )
                    }
                }
            }
        });
    };

    onStandardClickHandler = (courseKey, strandKey, standardKey) => {
        this.setState({
            tableProperties: {
                ...this.state.tableProperties,
                [courseKey]: {
                    ...this.state.tableProperties[courseKey],
                    subLevel: {
                        ...this.state.tableProperties[courseKey].subLevel,
                        [strandKey]: {
                            ...this.state.tableProperties[courseKey].subLevel[
                                strandKey
                            ],
                            subLevel: {
                                ...this.state.tableProperties[courseKey]
                                    .subLevel[strandKey].subLevel,
                                [standardKey]: getUpdatedStandardLevelState(
                                    this.state.tableProperties,
                                    courseKey,
                                    strandKey,
                                    standardKey
                                )
                            }
                        }
                    }
                }
            }
        });
    };

    /**
     * CLASSNAMES
     */
    getCourseLevelClassName = (courseKey) => {
        const { tableProperties } = this.state;
        let baseClass = css['course-holder'];

        if (tableProperties[courseKey].isExpanded) {
            baseClass = `${baseClass} ${css.expanded}`;
        }

        return baseClass;
    };

    getStrandLevelClassName = (courseKey, strandKey) => {
        const { tableProperties } = this.state;
        let baseClass = css['strand-content'];

        if (tableProperties[courseKey].subLevel[strandKey].isExpanded) {
            baseClass = `${baseClass} ${css.expanded}`;
        }

        return baseClass;
    };

    getStandardLevelClassName = (courseKey, strandKey, standardKey) => {
        const { tableProperties } = this.state;
        let baseClass = css['standard-wrapper'];

        if (
            tableProperties[courseKey].subLevel[strandKey].subLevel[standardKey]
                .isExpanded
        ) {
            baseClass = `${baseClass} ${css.expanded}`;
        }

        return baseClass;
    };

    getColorBandClassName = (hasStarted, accuracy) => {
        if (!hasStarted) return css.NOT_ASSIGNED;

        if (accuracy <= 20) {
            return css.RED;
        } else if (accuracy > 20 && accuracy <= 40) {
            return css.ORANGE;
        } else if (accuracy > 40 && accuracy <= 60) {
            return css.DARK_BLUE;
        } else if (accuracy > 60 && accuracy <= 80) {
            return css.LIGHT_BLUE;
        } else if (accuracy > 80 && accuracy <= 100) {
            return css.GREEN;
        }

        return '';
    };

    /**
     * RENDER
     */
    renderTableCourse = (publicationName, courseName, courseKey) => (
        <div className={css['course-wrapper']}>
            <div className={css['course-button-wrapper']}>
                <Button
                    className={css['course-button']}
                    icon={BackIcon}
                    onClick={() => this.onCourseClickHandler(courseKey)}
                />
            </div>
            <div className={css['course-name']}>
                {`${publicationName} - ${courseName}`}
            </div>
        </div>
    );

    renderTableStrandHeader = (strand, strandKey, courseKey) => {
        const { name, averageAccuracy } = strand;
        const hasStarted = averageAccuracy !== null;
        const colorBandClassname = `${
            css['color-band']
        } ${this.getColorBandClassName(hasStarted, averageAccuracy)}`;

        return (
            <div className={css['strand-header']}>
                <div className={colorBandClassname} />
                <div className={css['strand-title']}>
                    <Button
                        className={css['strand-button']}
                        icon={BackIcon}
                        onClick={() =>
                            this.onStrandClickHandler(courseKey, strandKey)
                        }
                    />
                    {name}
                </div>
            </div>
        );
    };

    renderTableHeaderInfoButton = (text, modalClassNameOverride = null) => (
        <div className={css['info-button-wrapper']}>
            <Button
                className={css['info-button']}
                icon={InfoIcon}
                onClick={() => {}}
            />
            <ContextModal
                className={`${css['info-context-modal']} ${modalClassNameOverride}`}
                show
                onClickOutsideHandler={() => {}}
            >
                {text}
            </ContextModal>
        </div>
    );

    getCompletedTextDiv = () => {
        const {
            infoCompletedTextOne,
            infoCompletedTextTwo,
            infoCompletedTextThree
        } = studentDataTranslations();
        const { onCompletedTextClickHandler } = this.props;

        return (
            <div>
                <p>{infoCompletedTextOne}</p>
                <button
                    className={css['completed-info-button']}
                    onClick={() => onCompletedTextClickHandler()}
                >
                    {infoCompletedTextTwo}
                </button>
                <p>{infoCompletedTextThree}</p>
            </div>
        );
    };

    renderTableStandardTitle = (assignmentResultType) => {
        const {
            standardText,
            completedText,
            allHighestInfoText,
            teacherAssignedInfoText,
            voluntaryInfoText,
            standardProgressText
        } = studentDataTranslations();

        const {
            HIGHEST,
            TEACHER_ASSIGNED,
            VOLUNTARY
        } = ASSIGNMENT_RESULT_TYPES;

        let infoText;

        switch (assignmentResultType) {
            case HIGHEST:
                infoText = allHighestInfoText;
                break;

            case TEACHER_ASSIGNED:
                infoText = teacherAssignedInfoText;
                break;

            case VOLUNTARY:
                infoText = voluntaryInfoText;
                break;

            default:
                infoText = '';
                break;
        }

        return (
            <div className={css['standard-title']}>
                <div className={css.long}>{standardText}</div>
                <div className={css.long}>
                    {standardProgressText}
                    {this.renderTableHeaderInfoButton(infoText)}
                </div>
                <div className={css.short}>
                    {completedText}
                    {this.renderTableHeaderInfoButton(
                        this.getCompletedTextDiv(),
                        css['right-edge-override']
                    )}
                </div>
            </div>
        );
    };

    getStandardHeaderText = (standard) => {
        if (standard.externalKey)
            return `${standard.externalKey} ${standard.description}`;

        return standard.description;
    };

    renderTableStandardContent = (
        standard,
        standardKey,
        strandKey,
        courseKey
    ) => {
        const { NOT_ASSIGNED, ASSIGNED } = ASSIGNMENT_STATUSES;
        const {
            averageAccuracy,
            status,
            learningJourneysCount,
            completedLearningJourneysCount
        } = standard;
        const completedText = `${completedLearningJourneysCount}/${learningJourneysCount}`;
        const hasStarted = !(status === NOT_ASSIGNED || status === ASSIGNED);
        const colorBandClass = `${
            css['color-band']
        } ${this.getColorBandClassName(hasStarted, averageAccuracy)}`;

        return (
            <div className={css['standard-content-border']}>
                <div className={colorBandClass} />
                <div className={css['standard-content']}>
                    <Button
                        className={css['standard-button']}
                        icon={BackIcon}
                        onClick={() =>
                            this.onStandardClickHandler(
                                courseKey,
                                strandKey,
                                standardKey
                            )
                        }
                    />
                    <span>{this.getStandardHeaderText(standard)}</span>
                </div>
                <div className={css['standard-separator']} />
                <div className={css['standard-completed']}>{completedText}</div>
            </div>
        );
    };

    renderTableTopic = (topic) => {
        const {
            name,
            id,
            learningJourneys,
            learningJourneysCount,
            completedLearningJourneysCount
        } = topic;
        const topicKey = `topic-${name}-${id}`;
        const completedText = `${completedLearningJourneysCount}/${learningJourneysCount}`;

        return (
            <div key={`topic-${name}-${id}`} className={css['topic-wrapper']}>
                <div className={css['color-band']} />
                <div className={css['topic-content']}>
                    <div className={css['topic-name']}>
                        <div className={css['topic-text']}>{name}</div>
                    </div>
                    <div className={css['topic-status']}>
                        {learningJourneys.map((journey) => (
                            <TopicBox
                                journey={journey}
                                key={`${topicKey}-${journey.id}`}
                            />
                        ))}
                    </div>
                    <div className={css['topic-completed']}>
                        {completedText}
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const {
            skillQuestsMasteryStudentData,
            assignmentResultType
        } = this.props;

        if (this.hasLoadedInitData()) {
            if (hasValidCourses(skillQuestsMasteryStudentData)) {
                return (
                    <div className={css['data-table']}>
                        {skillQuestsMasteryStudentData.reportData.courses.map(
                            (course) => {
                                const courseKey = getCourseKey(course);

                                return (
                                    <div
                                        key={courseKey}
                                        className={this.getCourseLevelClassName(
                                            courseKey
                                        )}
                                    >
                                        {this.renderTableCourse(
                                            course.publicationName,
                                            course.courseName,
                                            courseKey
                                        )}
                                        <div className={css.strands}>
                                            {course.strands.map((strand) => {
                                                const strandKey = getStrandKey(
                                                    strand
                                                );

                                                return (
                                                    <div
                                                        key={strandKey}
                                                        className={this.getStrandLevelClassName(
                                                            courseKey,
                                                            strandKey
                                                        )}
                                                    >
                                                        {this.renderTableStrandHeader(
                                                            strand,
                                                            strandKey,
                                                            courseKey
                                                        )}
                                                        <div
                                                            className={
                                                                css[
                                                                    'strand-wrapper'
                                                                ]
                                                            }
                                                        >
                                                            {this.renderTableStandardTitle(
                                                                assignmentResultType
                                                            )}
                                                            {strand.standards.map(
                                                                (standard) => {
                                                                    const standardKey = getStandardKey(
                                                                        standard
                                                                    );

                                                                    return (
                                                                        <div
                                                                            className={this.getStandardLevelClassName(
                                                                                courseKey,
                                                                                strandKey,
                                                                                standardKey
                                                                            )}
                                                                            key={
                                                                                standardKey
                                                                            }
                                                                        >
                                                                            {this.renderTableStandardContent(
                                                                                standard,
                                                                                standardKey,
                                                                                strandKey,
                                                                                courseKey
                                                                            )}
                                                                            {standard.topics.map(
                                                                                (
                                                                                    topic
                                                                                ) =>
                                                                                    this.renderTableTopic(
                                                                                        topic
                                                                                    )
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            }
                        )}
                    </div>
                );
            }
        }

        return null;
    }
}

export default styleable(css)(StudentDataTable);



// WEBPACK FOOTER //
// ./containers/skill-quests-mastery/student/components/student-data-table/student-data-table.js